/**
 * StoricoScansioniTab — Elenco globale delle scansioni consultazione
 * effettuate dagli utenti + KPI e top materiali. Export Excel.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle, MapPin, Download, Search, BarChart3, TrendingUp, AlertTriangle } from "lucide-react";
import api from "@/api";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const KIND_STYLE = {
  materiale: { label: "Materiale", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  ubicazione: { label: "Ubicazione", cls: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  error: { label: "Errore", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

export default function StoricoScansioniTab() {
  const [days, setDays] = React.useState(30);
  const [filtroEsito, setFiltroEsito] = React.useState("");   // "" | "found" | "notfound"
  const [filtroKind, setFiltroKind] = React.useState("");

  const { data: stats } = useQuery({
    queryKey: ["scan-stats", days],
    queryFn: () => api.get(`/magazzino/scansioni/stats?days=${days}`).then((r) => r.data),
  });
  const { data: rows = [] } = useQuery({
    queryKey: ["scan-log", filtroEsito, filtroKind],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "300" });
      if (filtroKind) params.set("kind", filtroKind);
      if (filtroEsito === "found") params.set("only_found", "true");
      if (filtroEsito === "notfound") params.set("only_found", "false");
      return api.get(`/magazzino/scansioni?${params}`).then((r) => r.data);
    },
  });

  const exportUrl = `${backendUrl}/api/magazzino/scansioni/export.xlsx?days=${days}`;

  return (
    <div className="space-y-5" data-testid="storico-scansioni-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="overline text-cyan-700">Storico Scansioni</div>
          <h2 className="font-display text-xl font-black">Analisi materiali più cercati</h2>
          <p className="text-slate-600 text-sm mt-1">
            Ogni scansione consultazione viene tracciata: analizza cosa cercano gli operatori e
            individua i barcode mancanti da aggiungere all'anagrafica.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Periodo</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="select-scan-days"
            className="px-3 py-1.5 border border-slate-300 bg-white text-sm font-semibold rounded-sm"
          >
            <option value={7}>Ultimi 7 giorni</option>
            <option value={30}>Ultimi 30 giorni</option>
            <option value={90}>Ultimi 90 giorni</option>
            <option value={180}>Ultimi 180 giorni</option>
          </select>
          <a
            href={exportUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="btn-export-scansioni"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-sm"
          >
            <Download className="w-4 h-4" /> Excel
          </a>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Scansioni totali" value={stats?.total ?? 0} icon={Search} testId="kpi-scan-tot" />
        <StatCard label="Riconosciute" value={stats?.found ?? 0} icon={CheckCircle2} good testId="kpi-scan-found" />
        <StatCard label="Non trovate" value={stats?.not_found ?? 0} icon={XCircle} bad testId="kpi-scan-notfound" />
        <StatCard
          label="Tasso di successo"
          value={
            stats?.total
              ? `${Math.round((stats.found / stats.total) * 100)}%`
              : "—"
          }
          icon={TrendingUp}
          testId="kpi-scan-rate"
        />
      </div>

      {/* Top materiali + top not-found */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopBox
          title="🥇 Materiali più scansionati"
          icon={BarChart3}
          rows={stats?.top_materiali || []}
          render={(m) => (
            <>
              <span className="font-bold text-slate-900 truncate flex-1">{m.nome}</span>
              <span className="font-mono font-black text-emerald-700">{m.count}×</span>
            </>
          )}
          empty="Ancora nessuna scansione riuscita nel periodo."
        />
        <TopBox
          title="⚠️ Codici NON trovati (candidati per anagrafica)"
          icon={AlertTriangle}
          rows={stats?.top_notfound || []}
          render={(r) => (
            <>
              <span className="font-mono font-bold text-slate-900 truncate flex-1">{r.code_scan}</span>
              <span className="font-mono font-black text-rose-700">{r.count}×</span>
            </>
          )}
          empty="Nessun codice non trovato: la copertura anagrafica è ottima!"
        />
      </div>

      {/* Filtri + tabella dettaglio */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-200 bg-slate-50">
          <div className="overline text-slate-600">Log dettagliato</div>
          <div className="ml-auto flex gap-2">
            <select
              value={filtroKind}
              onChange={(e) => setFiltroKind(e.target.value)}
              data-testid="filter-kind-scan"
              className="px-2 py-1 border border-slate-300 bg-white text-xs rounded-sm"
            >
              <option value="">Tutti i tipi</option>
              <option value="materiale">Solo materiali</option>
              <option value="ubicazione">Solo ubicazioni</option>
            </select>
            <select
              value={filtroEsito}
              onChange={(e) => setFiltroEsito(e.target.value)}
              data-testid="filter-esito-scan"
              className="px-2 py-1 border border-slate-300 bg-white text-xs rounded-sm"
            >
              <option value="">Tutti gli esiti</option>
              <option value="found">Solo TROVATE</option>
              <option value="notfound">Solo NON TROVATE</option>
            </select>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessuna scansione registrata.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Data</th>
                <th className="text-left p-3">Utente</th>
                <th className="text-left p-3">Tipo</th>
                <th className="text-left p-3">Codice</th>
                <th className="text-left p-3">Materiale / Ubicazione</th>
                <th className="text-left p-3">Esito</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = KIND_STYLE[r.kind] || KIND_STYLE.materiale;
                return (
                  <tr key={r.scan_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`log-${r.scan_id}`}>
                    <td className="p-3 text-xs whitespace-nowrap">{fmtDT(r.scanned_at)}</td>
                    <td className="p-3 text-xs">
                      <div className="font-bold">{r.user_name || "—"}</div>
                      <div className="text-slate-500">{r.user_email}</div>
                    </td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 border rounded-sm ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-xs">{r.code_scan}</td>
                    <td className="p-3 text-xs">
                      {r.materiale_nome ? (
                        <span className="font-bold">{r.materiale_nome}</span>
                      ) : r.ubicazione ? (
                        <span className="inline-flex items-center gap-1 text-cyan-700">
                          <MapPin className="w-3 h-3" /> {r.ubicazione}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {r.found ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" /> TROVATO
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700">
                          <XCircle className="w-3 h-3" /> NON TROVATO
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, good, bad, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {Icon && (
          <Icon className={`w-4 h-4 ${good ? "text-emerald-600" : bad ? "text-rose-600" : "text-slate-400"}`} />
        )}
      </div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}

function TopBox({ title, icon: Icon, rows, render, empty }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="p-3 border-b border-slate-200 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <div className="font-display font-bold text-sm">{title}</div>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-xs">{empty}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.slice(0, 10).map((r, i) => (
            <li key={i} className="p-2.5 flex items-center gap-3 text-xs">
              <span className="w-5 h-5 flex items-center justify-center bg-slate-100 rounded-full text-[10px] font-black text-slate-500">
                {i + 1}
              </span>
              {render(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
